<template>
  <div class="row">
    <div class="col-md-12" style="margin: -15px; font-size: small;">
      <div class="card">
        <div class="card-header" style="background-color: #DBECDB;">
          <h4 class="card-title">Gestión de Usuarios</h4>
          <div class="d-flex justify-content-between align-items-center">
            <p class="card-category">Lista de usuarios registrados en el sistema</p>
            <router-link
              v-if="hasPermission('user_create')"
              to="/users/create"
              class="btn btn-primary btn-round"
            >
              <i class="fa fa-plus"></i> Nuevo Usuario
            </router-link>
          </div>
        </div>
        <div class="card-body">
          <!-- Filtros de búsqueda -->
          <form class="row g-2 align-items-end mb-3" @submit.prevent="applyFilters">
            <div class="col-md-3">
              <label class="form-label mb-1">Nombres</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="filters.nombres"
                placeholder="Buscar por nombres"
              >
            </div>
            <div class="col-md-3">
              <label class="form-label mb-1">Apellidos</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="filters.apellidos"
                placeholder="Paterno y/o materno"
              >
            </div>
            <div class="col-md-3">
              <label class="form-label mb-1">Nro. de CI</label>
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="filters.nro_ci"
                placeholder="Buscar por CI"
              >
            </div>
            <div class="col-md-3 d-flex gap-1">
              <button type="submit" class="btn btn-sm btn-primary">
                <i class="fa fa-search"></i> Buscar
              </button>
              <button type="button" class="btn btn-sm btn-secondary" @click="clearFilters">
                <i class="fa fa-times"></i> Limpiar
              </button>
            </div>
          </form>

          <div v-if="loading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando...</span>
            </div>
          </div>

          <div v-else>
            <div class="table-responsive">
              <table class="table table-sm table-striped table-hover table-bordered" style="font-size: small;">
                <thead class="text-primary">
                  <tr style="line-height: 8px;">
                    <th>Usuario</th>
                    <th>Nombre.Completo</th>
                    <th>CI</th>
                    <th>Email</th>
                    <th>Cargo</th>
                    <th>Unidad</th>
                    <th>Área</th>
                    <th>Ventanilla</th>
                    <th>Estado</th>
                    <th>Roles</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in users" :key="user.id">
                    <td>{{ user.username }}</td>
                    <td>{{ user.nombres }} {{ user.paterno }} {{ user.materno }}</td>
                    <td>{{ user.nro_ci }}</td>
                    <td>{{ user.email }}</td>
                    <td>{{ user.cargo }}</td>
                    <td>{{ user.codigo_unidad }}</td>
                    <td>{{ user.codigo_area }}</td>
                    <td>{{ user.codigo_ventanilla }}</td>
                    <td>
                      <span
                        :class="user.estado === 'activo' ? 'badge bg-success' : 'badge bg-danger'"
                      >
                        {{ user.estado }}
                      </span>
                    </td>
                    <td>
                      <span
                        v-for="role in user.roles"
                        :key="role.id"
                        class="badge bg-info me-1"
                      >
                        {{ role.name }}
                      </span>
                    </td>
                    <td>
                      <div class="btn-group">
                        <router-link
                          v-if="hasPermission('user_update')"
                          :to="`/users/${user.id}/edit`"
                          class="btn btn-sm btn-warning"
                        >
                          <i class="fa fa-edit"></i>
                        </router-link>
                        <!--
                        <button
                          v-if="hasPermission('user_delete')"
                          @click="confirmDelete(user)"
                          class="btn btn-sm btn-danger"
                          :disabled="user.id === currentUserId"
                        >
                          <i class="fa fa-trash"></i>
                        </button>
                        -->
                        <BButton
                          v-if="hasPermission('user_update')"
                          @click="showModal(user)"
                          variant="danger"
                          size="sm"
                          class="me-1"
                        >
                          <i class="fa fa-key"></i>
                        </BButton>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="users.length === 0" class="text-center">
              <p>No hay usuarios registrados</p>
            </div>

            <!-- Paginación -->
            <div class="d-flex justify-content-between align-items-center mt-2">
              <div class="d-flex align-items-center">
                <span class="me-2">Mostrar</span>
                <select
                  v-model.number="perPage"
                  @change="onPerPageChange"
                  class="form-select form-select-sm"
                  style="width: auto;"
                >
                  <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
                </select>
                <span class="ms-3">Total: {{ totalRows }} usuarios</span>
              </div>

              <BPagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                size="sm"
                class="mb-0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para cambiar contraseña -->
    <BModal
      v-model="modalVisible"
      :title="modalTitle"
      size="xs"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <form @submit.prevent="savePass">
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label>Nueva Contraseña:</label>
              <input
                type="password"
                class="form-control"
                v-model="form.password"
                :class="{ 'is-invalid': errors.password }"
              >
              <div v-if="errors.password" class="invalid-feedback">
                {{ errors.password[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer mt-3">
          <BButton
            type="button"
            variant="secondary"
            @click="modalVisible = false"
            class="me-2"
          >
            Cancelar
          </BButton>
          <BButton
            type="submit"
            variant="primary"
            :disabled="loadingForm"
          >
            <span v-if="loadingForm" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isEditing ? 'Actualizar' : 'Guardar' }}
          </BButton>
        </div>
      </form>
    </BModal>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import { useAuthStore } from '../../stores/authStore';
import Swal from 'sweetalert2';
import { BModal, BButton, BPagination } from 'bootstrap-vue-3';

interface Role {
  id: number;
  name: string;
}

interface User {
  id: number;
  username: string;
  nombres: string;
  paterno: string;
  materno: string;
  nro_ci: string;
  email: string;
  cargo?: string;
  codigo_unidad: string;
  codigo_area: string;
  codigo_ventanilla?: string;
  id_ventanilla_asignada?: number | null;
  estado: string;
  roles: Role[];
  password?: string;
}

interface FormData {
  id: number | null;
  password: string;
}

export default {
  components: {
    BModal,
    BButton,
    BPagination
  },
  setup() {
    const users = ref<User[]>([]);
    const loading = ref(true);
    const authStore = useAuthStore();
    const modalVisible = ref(false);
    const isEditing = ref(false);
    const loadingForm = ref(false);
    const errors = ref<Record<string, string[]>>({});

    // Paginación
    const currentPage = ref(1);
    const perPage = ref(10);
    const totalRows = ref(0);

    // Filtros de búsqueda
    const filters = ref({
      nombres: '',
      apellidos: '',
      nro_ci: ''
    });
    const perPageOptions = [10, 25, 50, 100];

    const form = ref<FormData>({
      id: null,
      password: ''
    });

    const modalTitle = computed(() => {
      return 'Editar Password';
    });

    const currentUserId = computed(() => {
      return authStore.user?.user?.id;
    });

    const showModal = (user: User | null) => {
      errors.value = {};
      if (user) {
        isEditing.value = true;
        form.value = {
          id: user.id,
          password: ''
        };
      } else {
        isEditing.value = false;
        form.value = {
          id: null,
          password: ''
        };
      }
      modalVisible.value = true;
    };

    const fetchUsers = async () => {
      loading.value = true;
      try {
        const response = await axios.get('/users', {
          params: {
            page: currentPage.value,
            per_page: perPage.value,
            nombres: filters.value.nombres.trim(),
            apellidos: filters.value.apellidos.trim(),
            nro_ci: filters.value.nro_ci.trim()
          }
        });
        users.value = response.data.users;
        totalRows.value = response.data.pagination.total;
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los usuarios'
        });
      } finally {
        loading.value = false;
      }
    };

    const onPerPageChange = () => {
      if (currentPage.value === 1) {
        fetchUsers();
      } else {
        currentPage.value = 1; // el watch dispara la carga
      }
    };

    watch(currentPage, fetchUsers);

    const applyFilters = () => {
      if (currentPage.value === 1) {
        fetchUsers();
      } else {
        currentPage.value = 1; // el watch dispara la carga
      }
    };

    const clearFilters = () => {
      filters.value = { nombres: '', apellidos: '', nro_ci: '' };
      applyFilters();
    };

    const savePass = async () => {
      loadingForm.value = true;
      errors.value = {};
      try {
        if (!form.value.id) {
          throw new Error('ID de usuario no válido');
        }
        const response = await axios.post(
          `/users/${form.value.id}/edit_pass`,
          form.value,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );

        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: response.data.message
        });

        modalVisible.value = false;
        fetchUsers();
      } catch (error: any) {
        if (error.response && error.response.status === 422) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.response?.data?.message || error.message || 'Ocurrió un error al procesar la solicitud'
          });
        }
      } finally {
        loadingForm.value = false;
      }
    };

    const confirmDelete = (user: User) => {
      Swal.fire({
        title: '¿Está seguro?',
        text: `¿Desea eliminar al usuario ${user.username}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          deleteUser(user.id);
        }
      });
    };

    const deleteUser = async (id: number) => {
      try {
        await axios.get(`/users/${id}/delete`);
        Swal.fire({
          icon: 'success',
          title: 'Usuario eliminado',
          text: 'El usuario ha sido eliminado correctamente'
        });
        // Si la página actual quedó vacía, retroceder una
        if (users.value.length === 1 && currentPage.value > 1) {
          currentPage.value -= 1; // el watch recarga
        } else {
          await fetchUsers();
        }
      } catch (error: any) {
        console.error('Error al eliminar usuario:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo eliminar el usuario'
        });
      }
    };

    const hasPermission = (permission: string) => {
      return authStore.hasPermission(permission);
    };

    onMounted(() => {
      fetchUsers();
    });

    return {
      users,
      loading,
      currentUserId,
      confirmDelete,
      hasPermission,
      modalTitle,
      modalVisible,
      savePass,
      showModal,
      isEditing,
      loadingForm,
      form,
      errors,
      currentPage,
      perPage,
      perPageOptions,
      totalRows,
      onPerPageChange,
      filters,
      applyFilters,
      clearFilters,
    };
  }
};
</script>

<style scoped>
thead th {
  background-color: #D84315;
  color: #ffffff;
}
</style>
